import React from "react";

import { CheckCircle2, ChevronRight } from "lucide-react";

import BriefingProgress, { briefingRunSummary } from "./BriefingProgress";
import type { PlanFile } from "../../Types/AccountPlan";

interface Props {
  file: PlanFile;
  /** Open the draft on Review briefing. */
  onOpenDraft?: () => void;
}

/**
 * The briefing run on the Plan tab (product review 2026-11-01). While it is
 * written: the live checklist, section by section and question by question, where
 * the AE lands after "Yes, write the briefing". Once ready (a run often takes
 * under ten seconds): a summary of what it produced that opens to the same list.
 */
const BriefingRunCard: React.FC<Props> = ({ file, onOpenDraft }) => {
  if (file.sponsor_view_status === "processing")
    return (
      <div data-testid="briefing-run" data-state="running">
        <BriefingProgress file={file} />
      </div>
    );
  if (file.sponsor_view_status !== "ready") return null;
  const vN = `v${file.plan_version ?? file.version}`;
  const summary = briefingRunSummary(file);
  return (
    <details
      data-testid="briefing-run"
      data-state="ready"
      className="group rounded-xl border border-emerald-200 bg-emerald-50/40 text-sm"
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50">
        <ChevronRight
          className="h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-90"
          aria-hidden
        />
        <CheckCircle2
          className="h-4 w-4 shrink-0 text-emerald-600"
          aria-hidden
        />
        <span className="font-semibold text-gray-900">
          Briefing ready for {vN}
        </span>
        {summary && (
          <span
            className="min-w-0 flex-1 text-gray-600"
            data-testid="briefing-run-summary"
          >
            {summary}
          </span>
        )}
        {onOpenDraft && (
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              onOpenDraft();
            }}
            className="ml-auto rounded-md border border-purple-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#8538d4] hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            Open the briefing
          </button>
        )}
      </summary>
      {file.progress?.briefing?.steps && (
        <div className="border-t border-emerald-100 px-4 py-3">
          <BriefingProgress file={file} variant="done" />
        </div>
      )}
    </details>
  );
};

export default BriefingRunCard;
